import { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { get, some, chain, startsWith } from "lodash"
import { useTrack } from "helpers/analytics"
import { getQueryStringState, setQueryStringState } from "helpers/queryString"
import ModelSelector from "modules/model-selector"
import { getRawSelectedModelId } from "selectors/queryString"
import { useNavigate } from "react-router-dom"
import withOutletContext from "hocs/withOutletContext"
import { useRouterPath } from "hooks/router"
import ErrorModelAccess from "components/error-model-access"

function getPageKey(path) {
  // Builds a simili unique key for the page based on the routes, omitting the root and dynamic routes
  chain(path)
    .split("/")
    .filter(segment => !!segment && !startsWith(segment, ":"))
    .join("-")
    .value()
}

function setModel(navigate, model) {
  const qsState = getQueryStringState()
  setQueryStringState(navigate, { ...qsState, model })
}

function ModelAccessControl(props) {
  const navigate = useNavigate()
  const { setHeader, hocUserModels, hocModelId, WrappedComponent } = props

  const userHasAccessToModel = useMemo(
    () => !hocModelId || some(hocUserModels, model => model.id === hocModelId),
    [hocModelId, hocUserModels],
  )

  const { path } = useRouterPath()
  const track = useTrack(`${getPageKey(path)}-access-error`)

  useEffect(() => {
    if (!userHasAccessToModel) {
      track({ datasetId: hocModelId })
      setHeader({
        subnav: <ModelSelector
          warningMessage="Please change the risk model selection"
          disableUnderline
          noSpinner
          alternateLayout
          modelId="no-model-option"
          modelName="Select a different risk model"
          setModel={(model) => setModel(navigate, model)}
          additionalOption={{
            label: "Select a different risk model",
            value: "no-model-option",
          }}
        />,
      })
    }
  }, [userHasAccessToModel, setHeader, navigate, track, hocModelId])

  if (userHasAccessToModel) {
    return <WrappedComponent {...props} />
  }

  return <ErrorModelAccess modelId={hocModelId} />
}

ModelAccessControl.propTypes = {
  WrappedComponent: PropTypes.elementType.isRequired,
  hocModelId: PropTypes.string,
  hocUserModels: PropTypes.array.isRequired,
  setHeader: PropTypes.func.isRequired,
}

function withModelAccessControl(WrappedComponent) {
  const WithModelAccessControl = props => <ModelAccessControl WrappedComponent={WrappedComponent} {...props} />
  WithModelAccessControl.displayName = `withModelAccessControl(${WrappedComponent.displayName || WrappedComponent.name})`
  return WithModelAccessControl
}

function mapStateToProps(state, props) {
  // Props are prefixed with "hoc" to make sure it would not override any existing props in the WrappedComponent
  return {
    hocModelId: getRawSelectedModelId(state, props),
    hocUserModels: get(state, "global.data.models.data.models"),
  }
}

export default (WrappedComponent) => connect(mapStateToProps)(withOutletContext(withModelAccessControl(WrappedComponent)))
